import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './CellContextMenu.css';

interface CellContextMenuProps {
  visible: boolean;
  x: number;
  y: number;
  onCopy: () => void;
  onClose: () => void;
}

const CONTEXT_MENU_VIEWPORT_MARGIN_PX = 8;

export const CellContextMenu = ({ visible, x, y, onCopy, onClose }: CellContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: y, left: x });

  useLayoutEffect(() => {
    if (!visible || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const clampedLeft = Math.min(
      x,
      window.innerWidth - rect.width - CONTEXT_MENU_VIEWPORT_MARGIN_PX,
    );
    const clampedTop = Math.min(
      y,
      window.innerHeight - rect.height - CONTEXT_MENU_VIEWPORT_MARGIN_PX,
    );
    setPos({
      top: Math.max(CONTEXT_MENU_VIEWPORT_MARGIN_PX, clampedTop),
      left: Math.max(CONTEXT_MENU_VIEWPORT_MARGIN_PX, clampedLeft),
    });
  }, [visible, x, y]);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  useEffect(() => {
    if (visible && menuRef.current) {
      menuRef.current.focus();
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      className="contextMenu"
      style={{ top: pos.top, left: pos.left }}
    >
      <button type="button" role="menuitem" onClick={onCopy} className="contextMenuItem">
        Copier
      </button>
    </div>
  );
};
